import React, { useEffect, useState } from 'react'

import { interval, throttle } from 'rxjs'
import { range } from 'libraries/lodash'

import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { Scroll } from 'libraries/front-end-core/services/WindowService'
import useObservable from 'libraries/front-end-core/hooks/useObservable'

import { requestSearchResults } from 'services/SearchService'
import { Media } from 'services/WindowService'
import HeadingContainer from 'redux/containers/components/search/HeadingContainer'
import Results from 'components/search/Results'
import connect from 'components/shared/connect'
import ResponseConsumer from 'components/shared/ResponseConsumer'
import { ResultItemSkeleton } from './SearchResultsSkeleton'

const searchTitles = (flavour) => ({
  legislation: 'Search results - Legislation',
  qa: `Search results - Q&A${flavour === 'archive' ? ' (archived)' : ''}`,
  documents: 'Search results - Documents',
  ca: 'Search results - News'
})

const base = 150

const ScollingResults = ({ setRequestInflight, results }) => {
  useEffect(() => {
    setRequestInflight(false)
  }, [])

  return (
    <Results results={results} />
  )
}

const ConnectedScrollingResults = ({ setRequestInflight, ...props }) => {
  const response = useObservable(requestSearchResults(props), RESPONSE_PENDING)

  return (
    <ResponseConsumer
      response={response}
      content={() => <ScollingResults setRequestInflight={setRequestInflight} results={response.results} />}
      pendingComponent={() => range(0, 9).map((_, index) => <ResultItemSkeleton key={index} />)}
    />
  )
}

const MultiPageSearchResults = (props) => {
  const scroll = useObservable(Scroll.pipe(throttle(() => interval(200))), {})
  const mobile = useObservable(Media.Mobile, false)
  const tablet = useObservable(Media.Tablet, false)
  const [currentPage, setCurrentPage] = useState(1)
  const { searchResults, pageLimit, query, searchPhrase, searchType, flavour, setSearchResultsLoaded } = props
  const { results, resultCount, facets } = searchResults
  const title = searchTitles(flavour)[searchType] || 'Search results'
  const pageCount = Math.min(Math.ceil(resultCount / pageLimit), 1000)
  const [scrollingResults, setScrollingResults] = useState([])
  const [requestInflight, setRequestInflight] = useState(false)

  useEffect(() => {
    setSearchResultsLoaded(true)
    return () => setSearchResultsLoaded(false)
  }, [])

  useEffect(() => {
    const multiplier = mobile
      ? 7
      : tablet
        ? 5
        : 3
    const threshold = (base * multiplier) + 180
    const distanceFromBottom = ((scroll.scrollHeight - scroll.viewHeight) - scroll.scrollMain)
    const shouldRequestNextPage = !requestInflight &&
      (distanceFromBottom < threshold) &&
      (currentPage < pageCount)
    if (shouldRequestNextPage) {
      const { aol, flavour, pageLimit, query, searchPhrase, searchType } = props
      setRequestInflight(true)
      setScrollingResults(currentScrollingResults => [
        ...currentScrollingResults,
        {
          page: currentPage + 1,
          component: (
            <ConnectedScrollingResults
              key={currentPage}
              {...{
                aol,
                flavour,
                pageLimit,
                pageNumber: currentPage + 1,
                searchPhrase,
                searchType,
                query,
                setRequestInflight
              }}
            />
          )
        }
      ])
      setCurrentPage(currentPage => currentPage + 1)
    }
  }, [scroll?.scrollMain, mobile, tablet])

  return resultCount > 0
    ? (
      <section className='bg-page-background'>
        <div className='layout-width-constraint'>
          <div className='bb b--lite-grey mb7'>
            <HeadingContainer
              key='heading'
              pageNumber='all'
              {...{ facets, resultCount, pageLimit, query, searchPhrase, title }}
            />
            <Results {...{ results }} />
            <ul className='list-reset ma0 pa0'>
              {scrollingResults.map(({ component }) => component)}
            </ul>
          </div>
          {currentPage === pageCount && (
            <div className='tc f4 black'>End of results</div>
          )}
        </div>
      </section>
      )
    : (
      <section className='bg-page-background'>
        <div className='layout-width-constraint'>
          <HeadingContainer
            facets={searchResults.facets}
            key='heading'
            {...{ searchPhrase }}
            title='Search returned no results'
            resultCount={0}
          />
        </div>
      </section>
      )
}

const ResultsPage = connect( // eslint-disable-line
  ({ requestPage, pageNumber }) => requestPage(pageNumber)
)(Results)

export default MultiPageSearchResults
